import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  AlertCircle, AlertTriangle, Info, CheckCircle2, Clock, 
  Users, RefreshCw, ExternalLink,
  Bot, Shield, Calendar, Zap, CalendarDays, UserX,
  Package, GraduationCap, DollarSign, TrendingUp, Boxes, BookOpen, Wallet, LineChart
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Severity badge colors and icons
const severityConfig = {
  critical: { 
    color: 'bg-red-100 text-red-700 border-red-200', 
    icon: AlertCircle,
    label: 'Critical'
  },
  warning: { 
    color: 'bg-amber-100 text-amber-700 border-amber-200', 
    icon: AlertTriangle,
    label: 'Warning'
  },
  info: { 
    color: 'bg-blue-100 text-blue-700 border-blue-200', 
    icon: Info,
    label: 'Info'
  }
};

// Status badge colors
const statusConfig = {
  new: { color: 'bg-red-50 text-red-600 border-red-200', label: 'New' },
  reviewed: { color: 'bg-amber-50 text-amber-600 border-amber-200', label: 'Reviewed' },
  resolved: { color: 'bg-green-50 text-green-600 border-green-200', label: 'Resolved' },
  dismissed: { color: 'bg-slate-50 text-slate-600 border-slate-200', label: 'Dismissed' }
};

// Issue type descriptions - Timesheet
const timesheetIssueLabels = {
  missing_break: 'Missing Unpaid Break',
  duplicate_entry: 'Duplicate Entry',
  overlapping_entry: 'Overlapping Entries',
  zero_hours_when_scheduled: 'Zero Hours When Scheduled',
  missing_expected_entry: 'Missing Expected Entry',
  unusual_start_time: 'Unusual Start Time',
  unusual_end_time: 'Unusual End Time',
  high_daily_hours: 'High Daily Hours',
  mismatch_with_schedule: 'Schedule Mismatch'
};

// Issue type descriptions - Schedule
const scheduleIssueLabels = {
  unassigned_shift: 'Unassigned Shift',
  missing_physician_standard: 'Missing Physician',
  missing_physician_afterhours: 'Missing After-Hours',
  missing_saturday_coverage: 'Missing Saturday Coverage',
  understaffed_am: 'Understaffed AM',
  understaffed_closing: 'Understaffed Closing',
  double_booked: 'Double Booking',
  schedule_gap: 'Schedule Gap',
  overlapping_shifts: 'Overlapping Shifts',
  // Schedule anomalies
  long_shift: 'Long Shift (>10hrs)',
  early_start: 'Early Start (<8AM)',
  late_end: 'Late End (>9PM)',
  sunday_shift: 'Sunday Shift',
  missing_break_risk: 'Break Required',
  unusual_hours: 'Unusual Hours'
};

// Issue type descriptions - Supply
const supplyIssueLabels = {
  frequently_requested: 'Frequently Requested',
  overdue_pending: 'Overdue Pending',
  overdue_ordered: 'Overdue Ordered',
  urgent_unresolved: 'Urgent Unresolved',
  potential_reorder: 'Potential Reorder',
  repeated_urgent: 'Repeated Urgent'
};

// Issue type descriptions - Training
const trainingIssueLabels = {
  overdue_required: 'Overdue Required',
  incomplete_by_role: 'Incomplete by Role',
  new_hire_missing: 'New Hire Missing',
  version_reack_needed: 'Re-Acknowledgement Needed',
  certification_expiring: 'Certification Expiring'
};

// Issue type descriptions - Payroll
const payrollIssueLabels = {
  high_hours_anomaly: 'High Hours Anomaly',
  low_hours_anomaly: 'Low Hours Anomaly',
  missing_hourly_rate: 'Missing Hourly Rate',
  duplicate_payroll: 'Duplicate Paystub',
  missing_approved_timesheet: 'Missing Approved Timesheet',
  gross_pay_spike: 'Gross Pay Increase',
  gross_pay_drop: 'Gross Pay Decrease',
  net_pay_variance: 'Net Pay Variance'
};

// Issue type descriptions - Finance
const financeInsightLabels = {
  category_spike: 'Category Spending Up',
  category_drop: 'Category Spending Down',
  vendor_spend_high: 'High Vendor Spend',
  cash_outflow_high: 'Cash Outflow Up',
  cash_outflow_low: 'Cash Outflow Down',
  income_increase: 'Income Up',
  income_decrease: 'Income Down',
  top_expense_category: 'Top Expense Category'
};

export default function OperationsAssistant() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('timesheet');
  
  // Data state
  const [summary, setSummary] = useState(null);
  const [timesheetAlerts, setTimesheetAlerts] = useState([]);
  const [payPeriods, setPayPeriods] = useState([]);
  const [selectedPayPeriod, setSelectedPayPeriod] = useState(null);
  const [reviewPanel, setReviewPanel] = useState(null);
  
  // Schedule state
  const [scheduleAlerts, setScheduleAlerts] = useState(null);
  const [scheduleAnalysis, setScheduleAnalysis] = useState(null);
  
  // Supply state
  const [supplyAlerts, setSupplyAlerts] = useState(null);
  const [supplyAnalysis, setSupplyAnalysis] = useState(null);
  
  // Training state
  const [trainingAlerts, setTrainingAlerts] = useState(null);
  const [trainingAnalysis, setTrainingAnalysis] = useState(null);
  
  // Payroll state
  const [payrollAlerts, setPayrollAlerts] = useState(null);
  const [payrollAnalysis, setPayrollAnalysis] = useState(null);
  
  // Finance state
  const [financeInsights, setFinanceInsights] = useState(null);
  const [financeAnalysis, setFinanceAnalysis] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  
  // Analysis state
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisResults, setAnalysisResults] = useState(null);

  // Fetch summary
  const fetchSummary = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/assistant/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSummary(data);
      }
    } catch (err) {
      console.error('Error fetching summary:', err);
    }
  }, [token]);

  // Fetch pay periods
  const fetchPayPeriods = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/timesheets/pay-periods`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPayPeriods(data);
        if (data.length > 0 && !selectedPayPeriod) {
          setSelectedPayPeriod(data[0].id);
        }
      }
    } catch (err) {
      console.error('Error fetching pay periods:', err);
    }
  }, [token, selectedPayPeriod]);

  // Fetch timesheet alerts
  const fetchTimesheetAlerts = useCallback(async () => {
    if (!selectedPayPeriod) return;
    
    try {
      const res = await fetch(`${API}/api/assistant/timesheet/alerts?pay_period_id=${selectedPayPeriod}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTimesheetAlerts(data.all_alerts || []);
      }
    } catch (err) {
      console.error('Error fetching timesheet alerts:', err);
    }
  }, [token, selectedPayPeriod]);

  // Fetch review panel
  const fetchReviewPanel = useCallback(async () => {
    if (!selectedPayPeriod) return;
    
    try {
      const res = await fetch(`${API}/api/assistant/timesheet/review-panel?pay_period_id=${selectedPayPeriod}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setReviewPanel(data);
      }
    } catch (err) {
      console.error('Error fetching review panel:', err);
    }
  }, [token, selectedPayPeriod]);

  // Fetch schedule alerts
  const fetchScheduleAlerts = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/assistant/schedule/alerts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setScheduleAlerts(data);
      }
    } catch (err) {
      console.error('Error fetching schedule alerts:', err);
    }
  }, [token]);

  // Fetch supply alerts
  const fetchSupplyAlerts = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/assistant/supply/alerts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSupplyAlerts(data);
      }
    } catch (err) {
      console.error('Error fetching supply alerts:', err);
    }
  }, [token]);

  // Fetch training alerts
  const fetchTrainingAlerts = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/assistant/training/alerts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTrainingAlerts(data);
      }
    } catch (err) {
      console.error('Error fetching training alerts:', err);
    }
  }, [token]);

  // Fetch payroll alerts
  const fetchPayrollAlerts = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/assistant/payroll/alerts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPayrollAlerts(data);
      }
    } catch (err) {
      console.error('Error fetching payroll alerts:', err);
    }
  }, [token]);

  // Fetch finance insights
  const fetchFinanceInsights = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/assistant/finance/insights`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setFinanceInsights(data);
      }
    } catch (err) {
      console.error('Error fetching finance insights:', err);
    }
  }, [token]);

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchSummary(), 
        fetchPayPeriods(), 
        fetchScheduleAlerts(),
        fetchSupplyAlerts(),
        fetchTrainingAlerts(),
        fetchPayrollAlerts(),
        fetchFinanceInsights()
      ]);
      setLoading(false);
    };
    loadData();
  }, [fetchSummary, fetchPayPeriods, fetchScheduleAlerts, fetchSupplyAlerts, fetchTrainingAlerts, fetchPayrollAlerts, fetchFinanceInsights]);

  // Load pay period specific data
  useEffect(() => {
    if (selectedPayPeriod) {
      fetchTimesheetAlerts();
      fetchReviewPanel();
    }
  }, [selectedPayPeriod, fetchTimesheetAlerts, fetchReviewPanel]);

  // Analyze timesheets for pay period
  const analyzePayPeriod = async () => {
    if (!selectedPayPeriod) return;
    
    setAnalyzing(true);
    try {
      const res = await fetch(`${API}/api/assistant/timesheet/analyze-pay-period`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          pay_period_id: selectedPayPeriod,
          include_approved: false
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setAnalysisResults(data);
        toast.success(`Analyzed ${data.timesheets_analyzed} timesheets, found ${data.total_issues} issues`);
      } else {
        toast.error('Failed to analyze timesheets');
      }
    } catch (err) {
      toast.error('Error analyzing timesheets');
    } finally {
      setAnalyzing(false);
    }
  };

  // Create alerts from analysis
  const createAlertsForPayPeriod = async () => {
    if (!selectedPayPeriod) return;
    
    setAnalyzing(true);
    try {
      const res = await fetch(`${API}/api/assistant/timesheet/create-alerts-bulk`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          pay_period_id: selectedPayPeriod,
          include_approved: false
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`Created ${data.total_alerts_created} alerts`);
        fetchTimesheetAlerts();
        fetchReviewPanel();
        fetchSummary();
      } else {
        toast.error('Failed to create alerts');
      }
    } catch (err) {
      toast.error('Error creating alerts');
    } finally {
      setAnalyzing(false);
    }
  };

  // Analyze upcoming week schedules
  const analyzeUpcomingWeek = async () => {
    setAnalyzing(true);
    try {
      const res = await fetch(`${API}/api/assistant/schedule/upcoming-week`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setScheduleAnalysis(data);
        toast.success(`Analyzed ${data.days_analyzed} days, found ${data.total_issues} issues`);
      } else {
        toast.error('Failed to analyze schedules');
      }
    } catch (err) {
      toast.error('Error analyzing schedules');
    } finally {
      setAnalyzing(false);
    }
  };

  // Create schedule alerts
  const createScheduleAlerts = async () => {
    if (!scheduleAnalysis) {
      toast.error('Please analyze schedules first');
      return;
    }
    
    setAnalyzing(true);
    try {
      const res = await fetch(`${API}/api/assistant/schedule/create-alerts`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          start_date: scheduleAnalysis.week_start || scheduleAnalysis.start_date,
          end_date: scheduleAnalysis.week_end || scheduleAnalysis.end_date
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        fetchScheduleAlerts();
        fetchSummary();
      } else {
        toast.error('Failed to create alerts');
      }
    } catch (err) {
      toast.error('Error creating alerts');
    } finally {
      setAnalyzing(false);
    }
  };

  // Analyze supplies
  const analyzeSupplies = async () => {
    setAnalyzing(true);
    try {
      const res = await fetch(`${API}/api/assistant/supply/analyze`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setSupplyAnalysis(data);
        toast.success(`Supply analysis complete: found ${data.total_issues} issues`);
      } else {
        toast.error('Failed to analyze supplies');
      }
    } catch (err) {
      toast.error('Error analyzing supplies');
    } finally {
      setAnalyzing(false);
    }
  };

  // Create supply alerts
  const createSupplyAlerts = async () => {
    setAnalyzing(true);
    try {
      const res = await fetch(`${API}/api/assistant/supply/create-alerts`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        fetchSupplyAlerts();
        fetchSummary();
      } else {
        toast.error('Failed to create alerts');
      }
    } catch (err) {
      toast.error('Error creating alerts');
    } finally {
      setAnalyzing(false);
    }
  };

  // Analyze training
  const analyzeTraining = async () => {
    setAnalyzing(true);
    try {
      const res = await fetch(`${API}/api/assistant/training/analyze`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setTrainingAnalysis(data);
        toast.success(`Training analysis complete: found ${data.total_issues} issues`);
      } else {
        toast.error('Failed to analyze training');
      }
    } catch (err) {
      toast.error('Error analyzing training');
    } finally {
      setAnalyzing(false);
    }
  };

  // Create training alerts
  const createTrainingAlerts = async () => {
    setAnalyzing(true);
    try {
      const res = await fetch(`${API}/api/assistant/training/create-alerts`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        fetchTrainingAlerts();
        fetchSummary();
      } else {
        toast.error('Failed to create alerts');
      }
    } catch (err) {
      toast.error('Error creating alerts');
    } finally {
      setAnalyzing(false);
    }
  };

  // Analyze payroll
  const analyzePayroll = async () => {
    setAnalyzing(true);
    try {
      const res = await fetch(`${API}/api/assistant/payroll/analyze?pay_period_id=${selectedPayPeriod || ''}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setPayrollAnalysis(data);
        toast.success(`Payroll analysis complete: found ${data.total_issues} anomalies`);
      } else {
        toast.error('Failed to analyze payroll');
      }
    } catch (err) {
      toast.error('Error analyzing payroll');
    } finally {
      setAnalyzing(false);
    }
  };

  // Create payroll alerts
  const createPayrollAlerts = async () => {
    setAnalyzing(true);
    try {
      const res = await fetch(`${API}/api/assistant/payroll/create-alerts?pay_period_id=${selectedPayPeriod || ''}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        fetchPayrollAlerts();
        fetchSummary();
      } else {
        toast.error('Failed to create alerts');
      }
    } catch (err) {
      toast.error('Error creating alerts');
    } finally {
      setAnalyzing(false);
    }
  };

  // Analyze finance
  const analyzeFinance = async () => {
    setAnalyzing(true);
    try {
      const res = await fetch(`${API}/api/assistant/finance/analyze?year=${selectedYear}&month=${selectedMonth}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setFinanceAnalysis(data);
        toast.success(`Finance analysis complete: found ${data.total_insights} insights`);
      } else {
        toast.error('Failed to analyze finance');
      }
    } catch (err) {
      toast.error('Error analyzing finance');
    } finally {
      setAnalyzing(false);
    }
  };

  // Create finance insights
  const createFinanceInsights = async () => {
    setAnalyzing(true);
    try {
      const res = await fetch(`${API}/api/assistant/finance/create-insights`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ year: selectedYear, month: selectedMonth })
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        fetchFinanceInsights();
        fetchSummary();
      } else {
        toast.error('Failed to create insights');
      }
    } catch (err) {
      toast.error('Error creating insights');
    } finally {
      setAnalyzing(false);
    }
  };

  // Resolve alert
  const resolveAlert = async (alertId) => {
    try {
      const res = await fetch(`${API}/api/assistant/alerts/${alertId}/resolve`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Alert resolved');
        fetchTimesheetAlerts();
        fetchReviewPanel();
        fetchScheduleAlerts();
        fetchSummary();
      } else {
        toast.error('Failed to resolve alert');
      }
    } catch (err) {
      toast.error('Error resolving alert');
    }
  };

  // Mark alert as reviewed
  const markReviewed = async (alertId) => {
    try {
      const res = await fetch(`${API}/api/assistant/alerts/${alertId}`, {
        method: 'PATCH',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: 'reviewed' })
      });
      
      if (res.ok) {
        toast.success('Alert marked as reviewed');
        fetchTimesheetAlerts();
        fetchReviewPanel();
        fetchScheduleAlerts();
      } else {
        toast.error('Failed to update alert');
      }
    } catch (err) {
      toast.error('Error updating alert');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  const selectedPeriod = payPeriods.find(p => p.id === selectedPayPeriod);

  // Count alerts by module
  const timesheetModuleAlerts = summary?.modules?.find(m => m.module === 'timesheet');
  const scheduleModuleAlerts = summary?.modules?.find(m => m.module === 'schedule');
  const supplyModuleAlerts = summary?.modules?.find(m => m.module === 'supply');
  const trainingModuleAlerts = summary?.modules?.find(m => m.module === 'training');
  const payrollModuleAlerts = summary?.modules?.find(m => m.module === 'payroll');
  const financeModuleAlerts = summary?.modules?.find(m => m.module === 'finance');

  // Month options for finance
  const monthOptions = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  return (
    <div className="space-y-6" data-testid="operations-assistant">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
            <Bot className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Operations Assistant</h1>
            <p className="text-slate-500 text-sm">AI-powered operational monitoring and alerts</p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              fetchSummary();
              fetchTimesheetAlerts();
              fetchReviewPanel();
              fetchScheduleAlerts();
              fetchSupplyAlerts();
              fetchTrainingAlerts();
              fetchPayrollAlerts();
              fetchFinanceInsights();
            }}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Alerts</p>
                <p className="text-2xl font-bold text-slate-900">{summary?.total_alerts || 0}</p>
              </div>
              <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center">
                <AlertCircle className="h-5 w-5 text-slate-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">New Alerts</p>
                <p className="text-2xl font-bold text-red-600">{summary?.new_alerts || 0}</p>
              </div>
              <div className="h-10 w-10 rounded-lg bg-red-100 flex items-center justify-center">
                <Zap className="h-5 w-5 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Critical</p>
                <p className="text-2xl font-bold text-red-700">{summary?.critical_alerts || 0}</p>
              </div>
              <div className="h-10 w-10 rounded-lg bg-red-50 flex items-center justify-center">
                <AlertCircle className="h-5 w-5 text-red-700" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Active Modules</p>
                <p className="text-2xl font-bold text-slate-900">{summary?.modules?.length || 0}</p>
              </div>
              <div className="h-10 w-10 rounded-lg bg-violet-100 flex items-center justify-center">
                <Shield className="h-5 w-5 text-violet-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex-wrap h-auto gap-1">
          <TabsTrigger value="timesheet" data-testid="tab-timesheet">
            <Clock className="h-4 w-4 mr-2" />
            Timesheet
            {timesheetModuleAlerts?.total > 0 && (
              <Badge variant="destructive" className="ml-2 h-5 px-1.5">
                {timesheetModuleAlerts.total}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="schedule" data-testid="tab-schedule">
            <CalendarDays className="h-4 w-4 mr-2" />
            Schedule
            {scheduleModuleAlerts?.total > 0 && (
              <Badge variant="destructive" className="ml-2 h-5 px-1.5">
                {scheduleModuleAlerts.total}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="supply" data-testid="tab-supply">
            <Package className="h-4 w-4 mr-2" />
            Supply
            {supplyModuleAlerts?.total > 0 && (
              <Badge variant="destructive" className="ml-2 h-5 px-1.5">
                {supplyModuleAlerts.total}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="training" data-testid="tab-training">
            <GraduationCap className="h-4 w-4 mr-2" />
            Training
            {trainingModuleAlerts?.total > 0 && (
              <Badge variant="destructive" className="ml-2 h-5 px-1.5">
                {trainingModuleAlerts.total}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="payroll" data-testid="tab-payroll">
            <Wallet className="h-4 w-4 mr-2" />
            Payroll
            {payrollModuleAlerts?.total > 0 && (
              <Badge variant="destructive" className="ml-2 h-5 px-1.5">
                {payrollModuleAlerts.total}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="finance" data-testid="tab-finance">
            <LineChart className="h-4 w-4 mr-2" />
            Finance
            {financeModuleAlerts?.total > 0 && (
              <Badge variant="destructive" className="ml-2 h-5 px-1.5">
                {financeModuleAlerts.total}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* TIMESHEET TAB */}
        <TabsContent value="timesheet" className="space-y-6">
          {/* Pay Period Selector & Actions */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <CardTitle className="text-lg">Timesheet Review Assistant</CardTitle>
                  <CardDescription>
                    Analyze submitted timesheets before approval
                  </CardDescription>
                </div>
                
                <div className="flex items-center gap-3">
                  <Select value={selectedPayPeriod || ''} onValueChange={setSelectedPayPeriod}>
                    <SelectTrigger className="w-[280px]" data-testid="pay-period-select">
                      <SelectValue placeholder="Select pay period" />
                    </SelectTrigger>
                    <SelectContent>
                      {payPeriods.map(pp => (
                        <SelectItem key={pp.id} value={pp.id}>
                          {pp.start_date} to {pp.end_date} ({pp.status})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  <Button 
                    onClick={analyzePayPeriod} 
                    disabled={analyzing || !selectedPayPeriod}
                    variant="outline"
                    data-testid="analyze-btn"
                  >
                    {analyzing ? (
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Zap className="h-4 w-4 mr-2" />
                    )}
                    Analyze
                  </Button>
                  
                  <Button 
                    onClick={createAlertsForPayPeriod} 
                    disabled={analyzing || !selectedPayPeriod}
                    data-testid="create-alerts-btn"
                  >
                    Create Alerts
                  </Button>
                </div>
              </div>
            </CardHeader>
          </Card>

          {/* Analysis Results (if just ran) */}
          {analysisResults && (
            <Card className="border-violet-200 bg-violet-50/50">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-violet-600" />
                  Analysis Complete
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-sm text-slate-500">Timesheets Analyzed</p>
                    <p className="text-xl font-semibold">{analysisResults.timesheets_analyzed}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">With Issues</p>
                    <p className="text-xl font-semibold text-amber-600">{analysisResults.timesheets_with_issues}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Total Issues Found</p>
                    <p className="text-xl font-semibold text-red-600">{analysisResults.total_issues}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Clean Timesheets</p>
                    <p className="text-xl font-semibold text-green-600">
                      {analysisResults.timesheets_analyzed - analysisResults.timesheets_with_issues}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Review Panel */}
          {reviewPanel && (
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">Review Panel</CardTitle>
                    <CardDescription>
                      {selectedPeriod?.start_date} to {selectedPeriod?.end_date}
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-1">
                      <div className="h-3 w-3 rounded-full bg-red-500"></div>
                      <span>{reviewPanel.critical_count} Critical</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="h-3 w-3 rounded-full bg-amber-500"></div>
                      <span>{reviewPanel.warning_count} Warning</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="h-3 w-3 rounded-full bg-blue-500"></div>
                      <span>{reviewPanel.info_count} Info</span>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-500 mb-4">
                  {reviewPanel.submitted_timesheets} submitted timesheets • {reviewPanel.employees_with_issues} employees with issues • {reviewPanel.total_alerts} total alerts
                </p>
                
                {reviewPanel.review_items?.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <CheckCircle2 className="h-12 w-12 mx-auto mb-3 text-green-500" />
                    <p className="font-medium">No issues found</p>
                    <p className="text-sm">All timesheets are ready for approval</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {reviewPanel.review_items?.map((item) => {
                      const sevConfig = severityConfig[item.severity] || severityConfig.info;
                      const SevIcon = sevConfig.icon;
                      
                      return (
                        <div 
                          key={item.alert_id}
                          className="flex items-start gap-4 p-4 rounded-lg border bg-white hover:shadow-sm transition-shadow"
                          data-testid={`alert-item-${item.alert_id}`}
                        >
                          <div className={`h-10 w-10 rounded-lg ${sevConfig.color} flex items-center justify-center flex-shrink-0`}>
                            <SevIcon className="h-5 w-5" />
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <div>
                                <h4 className="font-medium text-slate-900">{item.title}</h4>
                                <p className="text-sm text-slate-500">
                                  {item.employee_name} • {item.date}
                                </p>
                              </div>
                              <Badge variant="outline" className={statusConfig[item.status]?.color}>
                                {statusConfig[item.status]?.label}
                              </Badge>
                            </div>
                            
                            <p className="text-sm text-slate-600 mt-2">{item.explanation}</p>
                            
                            <div className="flex items-center gap-2 mt-3">
                              <Badge variant="secondary" className="text-xs">
                                {timesheetIssueLabels[item.issue_type] || item.issue_type}
                              </Badge>
                              <span className="text-xs text-slate-400">•</span>
                              <span className="text-xs text-slate-500">Suggested: {item.suggested_action}</span>
                            </div>
                            
                            <div className="flex items-center gap-2 mt-3">
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => window.open(`/timesheets?highlight=${item.timesheet_id}`, '_blank')}
                              >
                                <ExternalLink className="h-3 w-3 mr-1" />
                                View Timesheet
                              </Button>
                              {item.status === 'new' && (
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => markReviewed(item.alert_id)}
                                >
                                  Mark Reviewed
                                </Button>
                              )}
                              <Button 
                                size="sm" 
                                variant="default"
                                onClick={() => resolveAlert(item.alert_id)}
                              >
                                <CheckCircle2 className="h-3 w-3 mr-1" />
                                Resolve
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* SCHEDULE TAB */}
        <TabsContent value="schedule" className="space-y-6">
          {/* Actions */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <CardTitle className="text-lg">Schedule Coverage Monitor</CardTitle>
                  <CardDescription>
                    Monitor upcoming schedules for coverage gaps and issues
                  </CardDescription>
                </div>
                
                <div className="flex items-center gap-3">
                  <Button 
                    onClick={analyzeUpcomingWeek} 
                    disabled={analyzing}
                    variant="outline"
                    data-testid="analyze-schedule-btn"
                  >
                    {analyzing ? (
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Zap className="h-4 w-4 mr-2" />
                    )}
                    Analyze Upcoming Week
                  </Button>
                  
                  <Button 
                    onClick={createScheduleAlerts} 
                    disabled={analyzing || !scheduleAnalysis}
                    data-testid="create-schedule-alerts-btn"
                  >
                    Create Alerts
                  </Button>
                </div>
              </div>
            </CardHeader>
          </Card>

          {/* Analysis Results */}
          {scheduleAnalysis && (
            <Card className="border-emerald-200 bg-emerald-50/50">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                  Schedule Analysis Complete
                </CardTitle>
                <CardDescription>
                  {scheduleAnalysis.week_start || scheduleAnalysis.start_date} to {scheduleAnalysis.week_end || scheduleAnalysis.end_date}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-sm text-slate-500">Days Analyzed</p>
                    <p className="text-xl font-semibold">{scheduleAnalysis.days_analyzed}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Total Schedules</p>
                    <p className="text-xl font-semibold">{scheduleAnalysis.total_schedules}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Issues Found</p>
                    <p className="text-xl font-semibold text-amber-600">{scheduleAnalysis.total_issues}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Critical Issues</p>
                    <p className="text-xl font-semibold text-red-600">{scheduleAnalysis.critical_count}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Schedule Alerts by Date */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Schedule Alerts</CardTitle>
                  <CardDescription>
                    Coverage gaps and scheduling issues
                  </CardDescription>
                </div>
                {scheduleAlerts && (
                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-1">
                      <div className="h-3 w-3 rounded-full bg-red-500"></div>
                      <span>{scheduleAlerts.all_alerts?.filter(a => a.severity === 'critical').length || 0} Critical</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="h-3 w-3 rounded-full bg-amber-500"></div>
                      <span>{scheduleAlerts.all_alerts?.filter(a => a.severity === 'warning').length || 0} Warning</span>
                    </div>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {!scheduleAlerts || scheduleAlerts.total_alerts === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <CalendarDays className="h-12 w-12 mx-auto mb-3 text-green-500" />
                  <p className="font-medium">No schedule issues</p>
                  <p className="text-sm">All upcoming schedules have adequate coverage</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {scheduleAlerts.by_date?.map((dayData) => (
                    <div key={dayData.date} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-5 w-5 text-slate-500" />
                          <h4 className="font-medium">{dayData.date} ({dayData.day_name})</h4>
                        </div>
                        <div className="flex items-center gap-2">
                          {dayData.critical_count > 0 && (
                            <Badge variant="destructive">{dayData.critical_count} Critical</Badge>
                          )}
                          {dayData.warning_count > 0 && (
                            <Badge variant="outline" className="border-amber-300 text-amber-700">{dayData.warning_count} Warning</Badge>
                          )}
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        {dayData.alerts.map((alert) => {
                          const sevConfig = severityConfig[alert.severity] || severityConfig.info;
                          const SevIcon = sevConfig.icon;
                          
                          return (
                            <div 
                              key={alert.id}
                              className="flex items-start gap-3 p-3 rounded-lg bg-slate-50"
                              data-testid={`schedule-alert-${alert.id}`}
                            >
                              <div className={`h-8 w-8 rounded-lg ${sevConfig.color} flex items-center justify-center flex-shrink-0`}>
                                <SevIcon className="h-4 w-4" />
                              </div>
                              
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-2">
                                  <h5 className="font-medium text-slate-900 text-sm">{alert.title}</h5>
                                  <Badge variant="outline" className={statusConfig[alert.status]?.color}>
                                    {statusConfig[alert.status]?.label}
                                  </Badge>
                                </div>
                                <p className="text-xs text-slate-600 mt-1">{alert.explanation}</p>
                                
                                <div className="flex items-center gap-2 mt-2">
                                  <Badge variant="secondary" className="text-xs">
                                    {scheduleIssueLabels[alert.issue_type] || alert.issue_type}
                                  </Badge>
                                </div>
                                
                                <div className="flex items-center gap-2 mt-2">
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    className="h-7 text-xs"
                                    onClick={() => window.open(`/schedules?date=${alert.affected_date}`, '_blank')}
                                  >
                                    <ExternalLink className="h-3 w-3 mr-1" />
                                    View Schedule
                                  </Button>
                                  {alert.status === 'new' && (
                                    <Button 
                                      size="sm" 
                                      variant="outline"
                                      className="h-7 text-xs"
                                      onClick={() => markReviewed(alert.id)}
                                    >
                                      Mark Reviewed
                                    </Button>
                                  )}
                                  <Button 
                                    size="sm" 
                                    variant="default"
                                    className="h-7 text-xs"
                                    onClick={() => resolveAlert(alert.id)}
                                  >
                                    <CheckCircle2 className="h-3 w-3 mr-1" />
                                    Resolve
                                  </Button>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* SUPPLY TAB */}
        <TabsContent value="supply" className="space-y-6">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <CardTitle className="text-lg">Supply Request Monitor</CardTitle>
                  <CardDescription>
                    Track supply patterns, overdue requests, and reorder signals
                  </CardDescription>
                </div>
                
                <div className="flex items-center gap-3">
                  <Button 
                    onClick={analyzeSupplies} 
                    disabled={analyzing}
                    variant="outline"
                    data-testid="analyze-supply-btn"
                  >
                    {analyzing ? (
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Zap className="h-4 w-4 mr-2" />
                    )}
                    Analyze Supplies
                  </Button>
                  
                  <Button 
                    onClick={createSupplyAlerts} 
                    disabled={analyzing}
                    data-testid="create-supply-alerts-btn"
                  >
                    Create Alerts
                  </Button>
                </div>
              </div>
            </CardHeader>
          </Card>

          {/* Supply Analysis Results */}
          {supplyAnalysis && (
            <Card className="border-orange-200 bg-orange-50/50">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-orange-600" />
                  Supply Analysis Complete
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-sm text-slate-500">Total Issues</p>
                    <p className="text-xl font-semibold">{supplyAnalysis.total_issues}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Critical</p>
                    <p className="text-xl font-semibold text-red-600">{supplyAnalysis.critical_count}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Warning</p>
                    <p className="text-xl font-semibold text-amber-600">{supplyAnalysis.warning_count}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Info</p>
                    <p className="text-xl font-semibold text-blue-600">{supplyAnalysis.info_count}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Supply Alerts */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Supply Alerts</CardTitle>
                  <CardDescription>
                    Pending requests, frequent items, and reorder signals
                  </CardDescription>
                </div>
                {supplyAlerts && (
                  <Badge variant="outline">{supplyAlerts.total_alerts} Alerts</Badge>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {!supplyAlerts || supplyAlerts.total_alerts === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Package className="h-12 w-12 mx-auto mb-3 text-green-500" />
                  <p className="font-medium">No supply issues</p>
                  <p className="text-sm">All supply requests are on track</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {supplyAlerts.all_alerts?.map((alert) => {
                    const sevConfig = severityConfig[alert.severity] || severityConfig.info;
                    const SevIcon = sevConfig.icon;
                    
                    return (
                      <div 
                        key={alert.id}
                        className="flex items-start gap-4 p-4 rounded-lg border bg-white hover:shadow-sm transition-shadow"
                        data-testid={`supply-alert-${alert.id}`}
                      >
                        <div className={`h-10 w-10 rounded-lg ${sevConfig.color} flex items-center justify-center flex-shrink-0`}>
                          <SevIcon className="h-5 w-5" />
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <h4 className="font-medium text-slate-900">{alert.title}</h4>
                              <p className="text-sm text-slate-500">
                                {alert.context?.item_name || 'Supply Item'}
                              </p>
                            </div>
                            <Badge variant="outline" className={statusConfig[alert.status]?.color}>
                              {statusConfig[alert.status]?.label}
                            </Badge>
                          </div>
                          
                          <p className="text-sm text-slate-600 mt-2">{alert.explanation}</p>
                          
                          <div className="flex items-center gap-2 mt-3">
                            <Badge variant="secondary" className="text-xs">
                              {supplyIssueLabels[alert.issue_type] || alert.issue_type}
                            </Badge>
                          </div>
                          
                          <div className="flex items-center gap-2 mt-3">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => window.open('/admin/supplies', '_blank')}
                            >
                              <ExternalLink className="h-3 w-3 mr-1" />
                              View Supplies
                            </Button>
                            {alert.status === 'new' && (
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => markReviewed(alert.id)}
                              >
                                Mark Reviewed
                              </Button>
                            )}
                            <Button 
                              size="sm" 
                              variant="default"
                              onClick={() => resolveAlert(alert.id)}
                            >
                              <CheckCircle2 className="h-3 w-3 mr-1" />
                              Resolve
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* TRAINING TAB */}
        <TabsContent value="training" className="space-y-6">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <CardTitle className="text-lg">Training Compliance Monitor</CardTitle>
                  <CardDescription>
                    Track training completion, certifications, and compliance gaps
                  </CardDescription>
                </div>
                
                <div className="flex items-center gap-3">
                  <Button 
                    onClick={analyzeTraining} 
                    disabled={analyzing}
                    variant="outline"
                    data-testid="analyze-training-btn"
                  >
                    {analyzing ? (
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Zap className="h-4 w-4 mr-2" />
                    )}
                    Analyze Training
                  </Button>
                  
                  <Button 
                    onClick={createTrainingAlerts} 
                    disabled={analyzing}
                    data-testid="create-training-alerts-btn"
                  >
                    Create Alerts
                  </Button>
                </div>
              </div>
            </CardHeader>
          </Card>

          {/* Training Analysis Results */}
          {trainingAnalysis && (
            <Card className="border-indigo-200 bg-indigo-50/50">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-indigo-600" />
                  Training Analysis Complete
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-sm text-slate-500">Total Issues</p>
                    <p className="text-xl font-semibold">{trainingAnalysis.total_issues}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Critical</p>
                    <p className="text-xl font-semibold text-red-600">{trainingAnalysis.critical_count}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Warning</p>
                    <p className="text-xl font-semibold text-amber-600">{trainingAnalysis.warning_count}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Info</p>
                    <p className="text-xl font-semibold text-blue-600">{trainingAnalysis.info_count}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Training Alerts */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Training Alerts</CardTitle>
                  <CardDescription>
                    Overdue training, missing completions, and compliance gaps
                  </CardDescription>
                </div>
                {trainingAlerts && (
                  <Badge variant="outline">{trainingAlerts.total_alerts} Alerts</Badge>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {!trainingAlerts || trainingAlerts.total_alerts === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <GraduationCap className="h-12 w-12 mx-auto mb-3 text-green-500" />
                  <p className="font-medium">No training issues</p>
                  <p className="text-sm">All staff are compliant with training requirements</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {trainingAlerts.all_alerts?.map((alert) => {
                    const sevConfig = severityConfig[alert.severity] || severityConfig.info;
                    const SevIcon = sevConfig.icon;
                    
                    return (
                      <div 
                        key={alert.id}
                        className="flex items-start gap-4 p-4 rounded-lg border bg-white hover:shadow-sm transition-shadow"
                        data-testid={`training-alert-${alert.id}`}
                      >
                        <div className={`h-10 w-10 rounded-lg ${sevConfig.color} flex items-center justify-center flex-shrink-0`}>
                          <SevIcon className="h-5 w-5" />
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <h4 className="font-medium text-slate-900">{alert.title}</h4>
                              <p className="text-sm text-slate-500">
                                {alert.context?.user_name || alert.context?.doc_title || 'Training'}
                              </p>
                            </div>
                            <Badge variant="outline" className={statusConfig[alert.status]?.color}>
                              {statusConfig[alert.status]?.label}
                            </Badge>
                          </div>
                          
                          <p className="text-sm text-slate-600 mt-2">{alert.explanation}</p>
                          
                          <div className="flex items-center gap-2 mt-3">
                            <Badge variant="secondary" className="text-xs">
                              {trainingIssueLabels[alert.issue_type] || alert.issue_type}
                            </Badge>
                          </div>
                          
                          <div className="flex items-center gap-2 mt-3">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => window.open('/admin/training-compliance', '_blank')}
                            >
                              <ExternalLink className="h-3 w-3 mr-1" />
                              View Training
                            </Button>
                            {alert.status === 'new' && (
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => markReviewed(alert.id)}
                              >
                                Mark Reviewed
                              </Button>
                            )}
                            <Button 
                              size="sm" 
                              variant="default"
                              onClick={() => resolveAlert(alert.id)}
                            >
                              <CheckCircle2 className="h-3 w-3 mr-1" />
                              Resolve
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* PAYROLL TAB */}
        <TabsContent value="payroll" className="space-y-6">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <CardTitle className="text-lg">Payroll Anomaly Detector</CardTitle>
                  <CardDescription>
                    Flag unusual patterns before payroll processing
                  </CardDescription>
                </div>
                
                <div className="flex items-center gap-3">
                  <Select value={selectedPayPeriod || ''} onValueChange={setSelectedPayPeriod}>
                    <SelectTrigger className="w-[240px]">
                      <SelectValue placeholder="Select pay period" />
                    </SelectTrigger>
                    <SelectContent>
                      {payPeriods.map(pp => (
                        <SelectItem key={pp.id} value={pp.id}>
                          {pp.start_date} to {pp.end_date}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  <Button 
                    onClick={analyzePayroll} 
                    disabled={analyzing}
                    variant="outline"
                    data-testid="analyze-payroll-btn"
                  >
                    {analyzing ? (
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Zap className="h-4 w-4 mr-2" />
                    )}
                    Analyze Payroll
                  </Button>
                  
                  <Button 
                    onClick={createPayrollAlerts} 
                    disabled={analyzing}
                    data-testid="create-payroll-alerts-btn"
                  >
                    Create Alerts
                  </Button>
                </div>
              </div>
            </CardHeader>
          </Card>

          {/* Payroll Analysis Results */}
          {payrollAnalysis && (
            <Card className="border-green-200 bg-green-50/50">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                  Payroll Analysis Complete
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-sm text-slate-500">Total Anomalies</p>
                    <p className="text-xl font-semibold">{payrollAnalysis.total_issues}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Critical</p>
                    <p className="text-xl font-semibold text-red-600">{payrollAnalysis.critical_count}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Warning</p>
                    <p className="text-xl font-semibold text-amber-600">{payrollAnalysis.warning_count}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Info</p>
                    <p className="text-xl font-semibold text-blue-600">{payrollAnalysis.info_count}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Payroll Alerts */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Payroll Alerts</CardTitle>
                  <CardDescription>
                    Hours anomalies, missing rates, and pay variances
                  </CardDescription>
                </div>
                {payrollAlerts && (
                  <Badge variant="outline">{payrollAlerts.total_alerts} Alerts</Badge>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {!payrollAlerts || payrollAlerts.total_alerts === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Wallet className="h-12 w-12 mx-auto mb-3 text-green-500" />
                  <p className="font-medium">No payroll anomalies</p>
                  <p className="text-sm">All payroll data looks normal</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {payrollAlerts.all_alerts?.map((alert) => {
                    const sevConfig = severityConfig[alert.severity] || severityConfig.info;
                    const SevIcon = sevConfig.icon;
                    
                    return (
                      <div 
                        key={alert.id}
                        className="flex items-start gap-4 p-4 rounded-lg border bg-white hover:shadow-sm transition-shadow"
                        data-testid={`payroll-alert-${alert.id}`}
                      >
                        <div className={`h-10 w-10 rounded-lg ${sevConfig.color} flex items-center justify-center flex-shrink-0`}>
                          <SevIcon className="h-5 w-5" />
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <h4 className="font-medium text-slate-900">{alert.title}</h4>
                              <p className="text-sm text-slate-500">
                                {alert.context?.user_name || 'Employee'}
                              </p>
                            </div>
                            <Badge variant="outline" className={statusConfig[alert.status]?.color}>
                              {statusConfig[alert.status]?.label}
                            </Badge>
                          </div>
                          
                          <p className="text-sm text-slate-600 mt-2">{alert.explanation}</p>
                          
                          <div className="flex items-center gap-2 mt-3">
                            <Badge variant="secondary" className="text-xs">
                              {payrollIssueLabels[alert.issue_type] || alert.issue_type}
                            </Badge>
                          </div>
                          
                          <div className="flex items-center gap-2 mt-3">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => window.open('/admin/payroll', '_blank')}
                            >
                              <ExternalLink className="h-3 w-3 mr-1" />
                              View Payroll
                            </Button>
                            {alert.status === 'new' && (
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => markReviewed(alert.id)}
                              >
                                Mark Reviewed
                              </Button>
                            )}
                            <Button 
                              size="sm" 
                              variant="default"
                              onClick={() => resolveAlert(alert.id)}
                            >
                              <CheckCircle2 className="h-3 w-3 mr-1" />
                              Resolve
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* FINANCE TAB */}
        <TabsContent value="finance" className="space-y-6">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <CardTitle className="text-lg">Finance Insight Engine</CardTitle>
                  <CardDescription>
                    Spending trends, variance alerts, and financial insights
                  </CardDescription>
                </div>
                
                <div className="flex items-center gap-3">
                  <Select value={String(selectedMonth)} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
                    <SelectTrigger className="w-[140px]">
                      <SelectValue placeholder="Month" />
                    </SelectTrigger>
                    <SelectContent>
                      {monthOptions.map(m => (
                        <SelectItem key={m.value} value={String(m.value)}>
                          {m.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                    <SelectTrigger className="w-[100px]">
                      <SelectValue placeholder="Year" />
                    </SelectTrigger>
                    <SelectContent>
                      {[2024, 2025, 2026].map(y => (
                        <SelectItem key={y} value={String(y)}>
                          {y}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  <Button 
                    onClick={analyzeFinance} 
                    disabled={analyzing}
                    variant="outline"
                    data-testid="analyze-finance-btn"
                  >
                    {analyzing ? (
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Zap className="h-4 w-4 mr-2" />
                    )}
                    Analyze
                  </Button>
                  
                  <Button 
                    onClick={createFinanceInsights} 
                    disabled={analyzing}
                    data-testid="create-finance-insights-btn"
                  >
                    Create Insights
                  </Button>
                </div>
              </div>
            </CardHeader>
          </Card>

          {/* Finance Analysis Results */}
          {financeAnalysis && (
            <Card className="border-cyan-200 bg-cyan-50/50">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-cyan-600" />
                  Finance Analysis Complete - {financeAnalysis.period}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div>
                    <p className="text-sm text-slate-500">Total Income</p>
                    <p className="text-xl font-semibold text-green-600">
                      ${financeAnalysis.summary?.total_income?.toLocaleString() || 0}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Total Expenses</p>
                    <p className="text-xl font-semibold text-red-600">
                      ${financeAnalysis.summary?.total_expenses?.toLocaleString() || 0}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Net</p>
                    <p className={`text-xl font-semibold ${(financeAnalysis.summary?.net || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      ${financeAnalysis.summary?.net?.toLocaleString() || 0}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Transactions</p>
                    <p className="text-xl font-semibold">{financeAnalysis.summary?.transaction_count || 0}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Insights</p>
                    <p className="text-xl font-semibold text-amber-600">{financeAnalysis.total_insights}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Finance Insights */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Finance Insights</CardTitle>
                  <CardDescription>
                    Spending patterns, trends, and variance alerts
                  </CardDescription>
                </div>
                {financeInsights && (
                  <Badge variant="outline">{financeInsights.total_insights} Insights</Badge>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {!financeInsights || financeInsights.total_insights === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <LineChart className="h-12 w-12 mx-auto mb-3 text-green-500" />
                  <p className="font-medium">No finance insights</p>
                  <p className="text-sm">Financial data is within normal ranges</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {financeInsights.all_insights?.map((insight) => {
                    const sevConfig = severityConfig[insight.severity] || severityConfig.info;
                    const SevIcon = sevConfig.icon;
                    
                    return (
                      <div 
                        key={insight.id}
                        className="flex items-start gap-4 p-4 rounded-lg border bg-white hover:shadow-sm transition-shadow"
                        data-testid={`finance-insight-${insight.id}`}
                      >
                        <div className={`h-10 w-10 rounded-lg ${sevConfig.color} flex items-center justify-center flex-shrink-0`}>
                          <SevIcon className="h-5 w-5" />
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <h4 className="font-medium text-slate-900">{insight.title}</h4>
                              <p className="text-sm text-slate-500">
                                {insight.context?.period || insight.context?.category || 'Finance'}
                              </p>
                            </div>
                            <Badge variant="outline" className={statusConfig[insight.status]?.color}>
                              {statusConfig[insight.status]?.label}
                            </Badge>
                          </div>
                          
                          <p className="text-sm text-slate-600 mt-2">{insight.explanation}</p>
                          
                          <div className="flex items-center gap-2 mt-3">
                            <Badge variant="secondary" className="text-xs">
                              {financeInsightLabels[insight.issue_type] || insight.issue_type}
                            </Badge>
                            {insight.context?.variance_pct && (
                              <Badge variant="outline" className="text-xs">
                                {insight.context.variance_pct > 0 ? '+' : ''}{insight.context.variance_pct.toFixed(0)}%
                              </Badge>
                            )}
                          </div>
                          
                          <div className="flex items-center gap-2 mt-3">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => window.open('/admin/finance', '_blank')}
                            >
                              <ExternalLink className="h-3 w-3 mr-1" />
                              View Finance
                            </Button>
                            {insight.status === 'new' && (
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => markReviewed(insight.id)}
                              >
                                Mark Reviewed
                              </Button>
                            )}
                            <Button 
                              size="sm" 
                              variant="default"
                              onClick={() => resolveAlert(insight.id)}
                            >
                              <CheckCircle2 className="h-3 w-3 mr-1" />
                              Resolve
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
